import { useTranslation } from 'react-i18next';

const LOCALE_MAP: Record<string, string> = { fr: 'fr-FR', en: 'en-US' };

export interface FeedDateParts {
    // e.g. "Sat 14 Sep · 08:30" -- the card's date eyebrow.
    primary: string;
    // e.g. "tomorrow" / "in 3 days" -- null when the date is further out,
    // in the past, or unscheduled.
    relative: string | null;
}

function startOfDay(d: Date): number {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

// Formats a feed item's date the way the redesigned cards want it: a compact
// weekday/date (with time only when the item actually has one) plus a short
// relative hint for anything happening within the coming week.
export function useFeedDate() {
    const { t, i18n } = useTranslation();
    const locale = LOCALE_MAP[i18n.language] ?? LOCALE_MAP.fr;

    return (value: string | null | undefined): FeedDateParts => {
        if (!value) {
            return { primary: t('common.unscheduled'), relative: null };
        }

        const date = new Date(value);
        if (isNaN(date.getTime())) {
            return { primary: value, relative: null };
        }

        const hasTime = /\d{2}:\d{2}/.test(value) && !(date.getHours() === 0 && date.getMinutes() === 0);

        let primary = new Intl.DateTimeFormat(locale, {
            weekday: 'short',
            day: 'numeric',
            month: 'short',
        }).format(date).replace(/\.$/, '');
        primary = primary.charAt(0).toUpperCase() + primary.slice(1);

        if (hasTime) {
            const time = new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' }).format(date);
            primary = `${primary} · ${time}`;
        }

        const days = Math.round((startOfDay(date) - startOfDay(new Date())) / 86_400_000);
        let relative: string | null = null;
        if (days === 0) relative = t('feed.today');
        else if (days === 1) relative = t('feed.tomorrow');
        else if (days >= 2 && days <= 7) relative = t('feed.inDays', { count: days });

        return { primary, relative };
    };
}
